import './NotificationList.css';

import {
  BasicSelect,
  Datagrid,
  DatagridCell,
  DatagridColumn,
  DatagridColumns,
  DatagridFooter,
  DatagridPagination,
  DatagridPlaceholder,
  DatagridRow,
  DatagridRows,
  PaginationStateChange,
  SelectOption
} from '@vmware-react/clarity';

import { HRow } from 'vrlcm-gui-components';
import { LCMNotification } from 'vrlcm-gui-types';
import NotificationIcon from '../notification-icon/NotificationIcon';
import React from 'react';
import { pageSizeList } from '../../../constants/notifications';
import {
  notificationNameToFriendlyName,
  isNotificationRead
} from 'vrlcm-gui-utils';
import { dateFormatters, formatDate } from 'i18n/i18nUtil';
import { l10n } from 'i18n/i18nUtil';

interface Props {
  notifications: LCMNotification[];
  loading: boolean;
  totalNotifications: number;
  pageSize: number;
  pageSizeChange: (pageSize: number) => void;
  currentPage: number;
  currentPageChange: (currentPage: number) => void;
  selected: string[];
  selectedChange: (newSelected?: string[]) => void;
  visitNotification: (notification: LCMNotification) => void;
}

const NotificationList: React.SFC<Props> = props => {
  const {
    notifications,
    loading,
    currentPage,
    currentPageChange,
    pageSize,
    pageSizeChange,
    totalNotifications,
    visitNotification,
    selected,
    selectedChange
  } = props;

  const onClickNotificationHandler = (notification: LCMNotification) => (
    e: React.MouseEvent<HTMLAnchorElement>
  ) => {
    e.preventDefault();
    visitNotification(notification);
  };

  const onPageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    pageSizeChange(e.target.value ? +e.target.value : pageSizeList[0]);

  const onStateChange = (pagination: PaginationStateChange) => {
    if (pagination.changed) {
      currentPageChange(pagination.data.currentPage);
    }
  };

  const multiSelection = {
    selected,
    onChange: selectedChange
  };

  return (
    <div className="notification-list-dg-wrapper">
      <Datagrid
        multiSelection={multiSelection}
        onStateChange={onStateChange}
        loading={loading}
        placeholder={
          <DatagridPlaceholder>
            {l10n('notifications.noNotificationsToShow')}
          </DatagridPlaceholder>
        }
        footer={
          <DatagridFooter
            pagination={
              <DatagridPagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalRows={totalNotifications}
              />
            }>
            {!loading && (
              <div className="dg-footer-description-wrapper">
                <div className="horizontal-space-grow" />
                <HRow marginRight={21}>
                  <HRow>
                    <label htmlFor="notificarion-list_page-size_select">
                      {l10n('show')}
                    </label>
                    <span className="pagesize-dropdown-wrapper">
                      <BasicSelect
                        id="notificarion-list_page-size_select"
                        name="notification-list_page-size"
                        error={false}
                        onChange={onPageSizeChange}
                        value={pageSize.toString()}>
                        {pageSizeList.map(p => (
                          <SelectOption key={p} value={p}>
                            {p}
                          </SelectOption>
                        ))}
                      </BasicSelect>
                    </span>
                    <span>{l10n('notifications.perPage')}</span>
                  </HRow>
                  {totalNotifications > 0 ? (
                    <span>
                      {((currentPage - 1) * pageSize + 1).toString()} -
                      {' ' +
                        (
                          (currentPage - 1) * pageSize +
                          notifications.length
                        ).toString() +
                        ' '}{' '}
                      {l10n('notifications.ofTotalNotifications', [
                        totalNotifications
                      ])}
                    </span>
                  ) : (
                    <span>{l10n('notifications.noNotifications')}</span>
                  )}
                </HRow>
              </div>
            )}
          </DatagridFooter>
        }
        columns={
          <DatagridColumns>
            <DatagridColumn>{l10n('notifications.type')}</DatagridColumn>
            <DatagridColumn>{l10n('notifications.message')}</DatagridColumn>
            <DatagridColumn>{l10n('notifications.description')}</DatagridColumn>
            <DatagridColumn>{l10n('notifications.createdOn')}</DatagridColumn>
            <DatagridColumn>{l10n('notifications.updatedOn')}</DatagridColumn>
            <DatagridColumn />
          </DatagridColumns>
        }
        rows={
          <DatagridRows>
            {notifications.map(n => (
              <DatagridRow
                key={n.vmid}
                rowKey={n.vmid}
                className={
                  isNotificationRead(n) ? 'unread-notification-row' : ''
                }>
                <DatagridCell>
                  <div className="notification-list__item__name">
                    <NotificationIcon size={24} name={n.name} level={n.level} />
                    <span>{notificationNameToFriendlyName(n.name)}</span>
                  </div>
                </DatagridCell>
                <DatagridCell>{n.shortDescription}</DatagridCell>
                <DatagridCell>{n.description}</DatagridCell>
                <DatagridCell>
                  {formatDate(n.createdOn, dateFormatters.SHORT)}
                </DatagridCell>
                <DatagridCell>
                  {formatDate(n.lastUpdatedOn, dateFormatters.SHORT)}
                </DatagridCell>
                <DatagridCell>
                  {n.redirectUrl && (
                    <a
                      href={n.redirectUrl}
                      target={
                        n.redirectUrl.startsWith('http') ? '_blank' : undefined
                      }
                      onClick={onClickNotificationHandler(n)}>
                      <HRow>
                        <span>{l10n('notifications.visit')}</span>
                        <clr-icon shape="arrow" dir="right" size={16} />
                      </HRow>
                    </a>
                  )}
                </DatagridCell>
              </DatagridRow>
            ))}
          </DatagridRows>
        }
      />
    </div>
  );
};

export default NotificationList;
